"use client";

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { mapUrl, trip, type LyricInterlude, type TripDay } from "../src/data/trip";

import { memories } from "../src/data/memories";

const dayFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Shanghai",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function getChinaDate() {
  return dayFormatter.format(new Date());
}

function Countdown() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const remaining = Math.max(0, new Date(trip.eventStart).getTime() - now);
  const values = [
    { value: Math.floor(remaining / 86_400_000), unit: "天" },
    { value: Math.floor((remaining / 3_600_000) % 24), unit: "时" },
    { value: Math.floor((remaining / 60_000) % 60), unit: "分" },
    { value: Math.floor((remaining / 1000) % 60), unit: "秒" },
  ];

  return (
    <section
      className="countdown-card"
      aria-label="Aqours Only 倒计时"
      style={{ "--countdown-image": `url(${trip.visuals.heroBackground})` } as React.CSSProperties}
    >
      <img src={trip.mascots.countdown} alt="举着一百分牌子的奇卡" />
      <div className="countdown-content">
        <div className="countdown-heading"><span>距离 Aqours Only</span><b>8月23日</b></div>
        {remaining > 0 ? (
          <div className="countdown-grid">
            {values.map((item) => <div key={item.unit}><strong>{String(item.value).padStart(2, "0")}</strong><small>{item.unit}</small></div>)}
          </div>
        ) : <p className="event-finished">这一天已经成为我们的共同回忆 ✦</p>}
        <p>{trip.event.venue} · {trip.event.time}</p>
      </div>
    </section>
  );
}

function ChikaTip({ children, image }: { children: React.ReactNode; image: string }) {
  return (
    <aside className="chika-tip">
      <img src={image} alt="奇卡表情" />
      <div><b>千歌说</b><p>{children}</p></div>
    </aside>
  );
}

function LyricLine({ lyric, className = "", accent }: { lyric: LyricInterlude; className?: string; accent?: string }) {
  return (
    <blockquote className={`lyric-line ${className}`.trim()} style={accent ? { "--day-accent": accent } as React.CSSProperties : undefined}>
      <span>✦</span>
      <div>
        <strong>{lyric.japanese}</strong>
        <p>{lyric.chinese}</p>
        <cite>— Aqours《{lyric.song}》</cite>
      </div>
    </blockquote>
  );
}

function Timeline({ day, isToday }: { day: TripDay; isToday: boolean }) {
  const currentIndex = useMemo(() => {
    if (!isToday) return -1;
    const chinaTime = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Shanghai",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date());
    const minutes = Number(chinaTime.slice(0, 2)) * 60 + Number(chinaTime.slice(3));
    let found = 0;
    day.stops.forEach((stop, index) => {
      if (/^\d{2}:\d{2}$/.test(stop.time)) {
        const [hour, minute] = stop.time.split(":").map(Number);
        if (hour * 60 + minute <= minutes) found = index;
      }
    });
    return found;
  }, [day, isToday]);

  return (
    <div className="timeline" style={{ "--day-accent": day.accent } as React.CSSProperties}>
      {day.stops.map((stop, index) => (
        <Fragment key={`${day.id}-${stop.time}-${stop.title}`}>
          <article className={`timeline-item ${index === currentIndex ? "now" : ""}`}>
            <div className="time-col"><time>{stop.time}</time>{stop.endTime && <small>至 {stop.endTime}</small>}</div>
            <div className="timeline-rail"><span>{stop.icon}</span></div>
            <div className={`stop-card ${stop.mascot ? "has-mascot" : ""}`}>
              <div className="stop-copy">
                <div className="stop-heading"><h3>{stop.title}</h3>{stop.tag && <b>{stop.tag}</b>}</div>
                <p>{stop.note}</p>
                {stop.guide && (
                  <div className="stop-guide">
                    <div className="guide-heading">
                      <strong>行程手记</strong>
                      {stop.guide.duration && <span>{stop.guide.duration}</span>}
                    </div>
                    <p className="guide-overview">{stop.guide.overview}</p>
                    <ol className="guide-route">
                      {stop.guide.route.map((step, stepIndex) => (
                        <li key={step}><i>{stepIndex + 1}</i><span>{step}</span></li>
                      ))}
                    </ol>
                  </div>
                )}
                {stop.mapQuery && <a href={mapUrl(stop.mapQuery)} target="_blank" rel="noreferrer">导航 ↗</a>}
              </div>
              {stop.mascot && <img src={stop.mascot} alt="奇卡表情" />}
            </div>
          </article>
          {stop.lyric && <LyricLine lyric={stop.lyric} className="lyric-interlude" />}
        </Fragment>
      ))}
    </div>
  );
}

export default function TripApp() {
  const [view, setView] = useState<"journey" | "memories">("journey");
  const scrollPositions = useRef({ journey: 0, memories: 0 });
  const switchView = (next: "journey" | "memories") => {
    if (next === view) return;
    scrollPositions.current[view] = window.scrollY;
    setView(next);
  };
  useLayoutEffect(() => {
    window.scrollTo({ top: scrollPositions.current[view], behavior: "instant" });
  }, [view]);
  const today = getChinaDate();
  const todayIndex = trip.days.findIndex((day) => day.date === today);
  const [activeDay, setActiveDay] = useState(todayIndex >= 0 ? todayIndex : 0);
  const day = trip.days[activeDay];
  const tipImage = day.id === 1 ? trip.mascots.day1 : day.id === 2 ? trip.mascots.day2 : trip.mascots.day3;
  const tipText = day.id === 1 ? trip.tips.general : day.id === 2 ? trip.tips.event : trip.tips.walk;

  return (
    <main>
      <div hidden={view !== "journey"} id="journey-view">
      <header className="hero" id="home" style={{ "--hero-image": `url(${trip.visuals.heroBackground})` } as React.CSSProperties}>
        <div className="hero-inner">
          <div className="hero-topline">
            <div className="series-mark"><img src={trip.visuals.sunshineLogo} alt="LoveLive! Sunshine!!" /></div>
          </div>
          <div className="hero-title-block">
            <h1><span>重庆</span><strong>Aqours Trip</strong></h1>
            <div className="hero-meta"><span>08.22—08.24</span><i /><span>Aqours Only</span></div>
          </div>
          <LyricLine lyric={trip.lyrics.hero} className="hero-lyric" />
        </div>
      </header>

      <section className="meetup shell">
        <div className="compact-heading"><h2>出发</h2><span>8月22日</span></div>
        <div className="member-grid">
          {trip.members.map((member) => (
            <article className="member-card" key={member.name}>
              <div className="member-icon">{member.icon}</div>
              <div><strong>{member.name}</strong><span>{member.from} → {member.destination}</span></div>
              <time>{member.arrival}</time>
            </article>
          ))}
        </div>
        <div className="gather-line"><span>四人到齐</span><b>↓</b></div>
        <a
          className="base-card"
          href={mapUrl(trip.base.mapQuery)}
          target="_blank"
          rel="noreferrer"
          style={{ "--base-image": `url(${trip.base.image})` } as React.CSSProperties}
        >
          <div className="base-icon">⌂</div><div><h3>{trip.base.name}</h3><p>{trip.base.location}</p></div><span>导航 ↗</span>
        </a>
      </section>

      <div className="shell"><Countdown /></div>

      <div className="shell memory-banner-wrap">
        <button className="memory-banner" onClick={() => switchView("memories")}>
          <img src={trip.mascots.friends} alt="" />
          <span className="memory-banner-copy"><strong>旅行回忆</strong><span>照片之外，也把故事留下来。</span></span>
          <span className="memory-banner-arrow" aria-hidden="true">翻翻手记 ↗</span>
        </button>
      </div>

      <section className="journey" id="journey">
        <div className="shell">
          <div className="compact-heading journey-heading"><h2>三日行程</h2>{todayIndex >= 0 && <span>今天是第 {todayIndex + 1} 天</span>}</div>
          <div className="day-tabs" role="tablist" aria-label="三日行程切换">
            {trip.days.map((item, index) => (
              <button key={item.id} role="tab" aria-selected={activeDay === index} style={{ "--tab-accent": item.accent } as React.CSSProperties} className={`${activeDay === index ? "active" : ""} ${today === item.date ? "today" : ""}`} onClick={() => setActiveDay(index)}>
                <small>第 {item.id} 天</small><strong>{item.dateLabel}</strong><span>{item.weekday}</span>
              </button>
            ))}
          </div>
          <div className="day-summary" style={{ "--day-accent": day.accent } as React.CSSProperties}>
            <div><small>第 {day.id} 天 · {day.dateLabel}</small><h2>{day.theme}</h2><p>{day.subtitle}</p></div><b>✦</b>
          </div>
          {day.lyric && <LyricLine lyric={day.lyric} className="day-lyric" accent={day.accent} />}
          <ChikaTip image={tipImage}>{tipText}</ChikaTip>
          <Timeline day={day} isToday={today === day.date} />
        </div>
      </section>

      </div>
      <section hidden={view !== "memories"} id="memories-view" className="memories-view" aria-labelledby="memories-title">
        <header className="memories-cover" style={{ "--memory-image": `url(${trip.visuals.heroBackground})` } as React.CSSProperties}>
          <div className="shell"><button className="memory-back" onClick={() => switchView("journey")}>← 返回行程</button><span>重庆 · 2026 盛夏</span><h1 id="memories-title">旅行回忆</h1><p>三天的旅程，慢慢写下来的故事。</p></div>
        </header>
        <div className="shell memories-content">
          {memories.map(memory => (
            <article className="repo-article" key={memory.id} aria-labelledby={`repo-${memory.id}`}>
              <header className="repo-heading">
                <div className="repo-meta"><span>旅行手记 / REPO</span><span>2026 · 夏</span></div>
                <h2 id={`repo-${memory.id}`}>{memory.title}</h2>
                <div className="repo-byline"><img src={trip.mascots.day1} alt="奇卡表情" /><span>{memory.author}<small>重庆 Aqours Only · 旅行记录</small></span></div>
              </header>
              <blockquote className="repo-pullquote">{memory.excerpt}</blockquote>
              <div className="repo-body">
                {memory.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              </div>
              <div className="repo-signature"><img src={trip.mascots.friends} alt="好兄弟奇卡表情" /><span>— {memory.author}</span></div>
            </article>
          ))}
          <button className="repo-return" onClick={() => switchView("journey")}>← 回到三日行程</button>
          <p className="memories-note">青空 · 冬叶 · 寝屋 · 琪露诺</p>
        </div>
      </section>

      <footer><img src={trip.mascots.friends} alt="好兄弟表情包" /><div><LyricLine lyric={trip.lyrics.footer} className="footer-lyric" /><small>重庆 · 2026 盛夏<br />青空 · 冬叶 · 寝屋 · 琪露诺</small></div></footer>

    </main>
  );
}
